import { Button } from '@ibsys/kit';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTablePresets } from '@/entities/settings';
import { PresetOfferBar } from './PresetOfferBar';

type Props = {
  /**
   * Ключ рабочей записи в `settings.tables`: имя таблицы (например, `'dialogsListV2'`) либо
   * ключ раздела — у таблицы с разделами активный пресет и вид свои у каждого из них.
   */
  sectionKey: string;
};

/**
 * Предложение сохранить донастроенный системный пресет личной копией с отказом от донастройки.
 *
 * Показывается, пока пользователь без разрешения на управление системными пресетами держит
 * применённый системный пресет донастроенным: сам пресет общий, записать в него донастройку
 * такому пользователю нечем — распоряжаться он может только своей копией. Копия сохраняется
 * из текущего вида и сразу становится активной, поэтому предложение исчезает само; отмена
 * изменений — второй исход: вид возвращается к пресету.
 */
export const SystemPresetCopyOffer: FC<Props> = ({ sectionKey }) => {
  const { t } = useTranslation('tablePresets');
  const { activePreset, isDirty, savePresetAs, apply } = useTablePresets(sectionKey);

  const isCopyOffered = Boolean(activePreset?.isSystem) && !activePreset?.isEditable && isDirty;

  const handleSaveCopy = useCallback(() => {
    if (!activePreset) return;
    savePresetAs(t('systemPresetCopySuffix', { name: activePreset.name }));
  }, [activePreset, savePresetAs, t]);

  // Отказ от донастройки: применяем пресет заново — вид возвращается к нему, сам пресет
  // остаётся применённым, копия не создаётся, и предложение исчезает вместе с донастройкой.
  const handleUndoChanges = useCallback(() => {
    if (!activePreset) return;
    apply(activePreset.id);
  }, [activePreset, apply]);

  if (!isCopyOffered) return null;

  return (
    <PresetOfferBar hint={t('systemPresetCopyHint')}>
      <Button size="sm" variant="ghost" onClick={handleUndoChanges}>
        {t('undoChanges')}
      </Button>
      <Button size="sm" onClick={handleSaveCopy}>
        {t('systemPresetCopy')}
      </Button>
    </PresetOfferBar>
  );
};
